import Dialog from '@pelckmans/elementis/components/dialog';
import ListSkeleton, { SkeletonListProps } from './ListSkeleton';
import TableSkeleton, { SkeletonTableProps } from './TableSkeleton';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type ListProps = {
  variant: 'list';
  headerTitle: string;
  bodyText: ReactNode;
  cta?: ReactNode;
  skeletonProps?: Omit<SkeletonListProps, 'isLoading'>;
};

type TableProps = {
  variant: 'table';
  headerTitle: string;
  bodyText: ReactNode;
  cta?: ReactNode;
  skeletonProps?: Omit<SkeletonTableProps, 'isLoading'>;
  id: string;
};

type Props = ListProps | TableProps;

/**
 * EmptyState component for displaying empty state messages.
 *
 * @note This component is only used for empty states (when there is no data to display).
 * For loading states, use the LoadingState component instead.
 */
function EmptyState(props: Props) {
  const { t } = useTranslation();
  const { variant, headerTitle, bodyText, cta, skeletonProps } = props;

  return (
    <div className={classNames('empty-loading-state', `empty-loading-state--${variant}`)} data-testid="empty-state">
      {variant === 'list' && <ListSkeleton {...skeletonProps} isLoading={false} />}
      {variant === 'table' && <TableSkeleton {...skeletonProps} isLoading={false} id={props.id} />}

      <Dialog
        id="empty-state-dialog"
        size="sm"
        className="empty-state-dialog"
        aria-label={t('EMPTY_STATES.A11Y_LABEL')}
      >
        <Dialog.Header title={headerTitle} />
        <Dialog.Body>{bodyText}</Dialog.Body>
        {cta && <Dialog.Footer buttonGroupPropsRight={{ 'aria-label': undefined, role: undefined, children: cta }} />}
      </Dialog>
    </div>
  );
}

export default EmptyState;
